import React, { useState, useMemo, useEffect, useCallback, useRef } from 'react'
import { Accordion, Table, Button, Footer, useWindowContext, noop } from '@iqueue/ui-kit'
import moment from 'moment'
import InspectionPresenter from '../../api/InspectionPresenter'
import useTranslate, { t } from '../../hooks/useTranslate'
import './translations'

const icons = {
  year: 'event',
  month: 'calendar_today',
  organization: 'corporate_fare',
  factory: 'factory',
  field: 'water_pump',
  collectionPoint: 'oil_barrel',
  well: 'water_pump',
  log: 'person',
}

export default function InspectionLogsPage() {
  const [response, setResponse] = useState(null)
  const [entries, setEntries] = useState([])
  const tableRef = useRef()
  const { togglePaddings, setTitle, toggleScrollbars } = useWindowContext()
  const locale = useTranslate()

  const parseInspectionLogs = useCallback((logs) => {
    return logs.map(log => ({
      id: log.id,
      title: t('inspectionLog.title', { date: moment(log.createdAt).format('YYYY-MM-DD') }),
      subtitle: `${ t('inspectionLog.executor') }: ${ log.user.profile.name } ${ log.user.profile.surname }`,
      content: {
        logs: [{
          well: `${ t('inspectionLog.action') }: ${ log.action }`,
          dateTime: `${ t('inspectionLog.dateTime') }: ${ moment(log.createdAt).format('YYYY-MM-DD') } - ${ moment(log.createdAt).format('HH:mm') }`,
          userInfo: `${ t('inspectionLog.userInfo') }: ${ log.user.profile.name } ${ log.user.profile.surname }`,
          status: t(`inspectionLog.status.${ log.user.enabled ? 'active' : 'inactive' }`),
        }],
      },
    }))
  }, [locale, t])

  const parseNodes = useCallback((node, children) => {
    return children.map(child => {
      const isWell = child.type === "WELL"
      return {
        id: child.id,
        title: `${ child.name }`,
        subtitle: t(`type.node.${ child?.type }`),
        icon: icons[child.type.toLowerCase()] || 'device_hub',
        children: isWell && child?.inspectionLogs ? parseInspectionLogs(child.inspectionLogs) : parseNodes(child, child.children || []),
      }
    })
  }, [parseInspectionLogs])

  const parseDataToAccordion = useCallback((data) => {
    return Object.entries(data).map(([year, months]) => {
      return {
        id: year,
        title: `${ t('inspectionLog.nodes.year') } (${ year })`,
        icon: icons.year,
        children: Object.entries(months).map(([month, organizationDetails]) => ({
          id: `${ year }-${ month }`,
          title: `${ t('inspectionLog.nodes.month') } (${ moment(month, 'MM').format('MMMM') })`,
          icon: icons.month,
          children: [{
            id: organizationDetails.id,
            title: `${ organizationDetails.name } (${ t(`inspectionLog.nodes.${ organizationDetails.type.toLowerCase() }`) })`,
            icon: icons.organization,
            children: parseNodes(organizationDetails, organizationDetails.children || []),
          }],
        })),
      }
    })
  }, [parseNodes, t, locale])

  const schema = useMemo(() => [
    {
      key: 'well',
      title: t('inspectionLog.action'),
    }, {
      key: 'user',
      title: t('inspectionLog.executor'),
      width: '20rem',
    }, {
      key: 'dateTime',
      title: t('inspectionLog.dateTime'),
      width: '20rem',
      render: (_, row) => `${ row.date } - ${ row.time }`,
    }, {
      key: 'userInfo',
      title: t('inspectionLog.userInfo'),
      render: (_, row) => (
        <div>
          <strong>Имя:</strong> { row.userInfo.name } <br/>
        </div>
      ),
    },
  ], [t])

  const renderContent = useCallback(async (id, item) => {
    if (item.children && item.children.length > 0) {
      return <Accordion
        entries={ item.children }
        titleKey="title"
        renderContent={ renderContent }
        render={ (v) => {
          return {
            id: item.id,
            title: item.title || 'Нет данных',
          }
        } }
        icon={ item.icon }
      />
    } else if (item.content) {
      const { logs } = item.content
      return <Table
        ref={ tableRef }
        schema={ schema }
        entries={ [] }
        minWidth="70rem"
      >
        <Footer className="iq-pagination">
          <Button
            primary
            onClick={ () => tableRef.current.exportToExcel('report', 'report') }
          >
            { t('inspectionLog.download') }
          </Button>
        </Footer>
      </Table>
    } else {
      return <div>Нет данных</div>
    }
  }, [])

  const translatedTitle = useMemo(() => t('menu.inspectionLog'), [locale])

  useEffect(() => {
    setTitle(translatedTitle)
    togglePaddings(false)
    toggleScrollbars(true)
  }, [translatedTitle])

  useEffect(() => {
    const fetchData = async () => {
      await InspectionPresenter.get('journal').then(setResponse)
    }
    fetchData().catch(error => console.error(error))
  }, [])

  useEffect(() => {
    if (response) {
      const parsedEntries = parseDataToAccordion(response)
      setEntries(parsedEntries)
    }
  }, [response])

  return <div className="main-accordion-box">
    <Accordion entries={ entries } titleKey="title" renderContent={ renderContent }/>
  </div>
}
